import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import DashboardLayout from '../../../components/layout/layout';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate, useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { useGetCompanyById } from "../../../hooks/api/user";
import { useGetCompanyUsersById } from "../../../hooks/api/user";

export interface CPUsersDetails {
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  pwDate: string;
  email: string;
  phone: string;
  numLogons: string;
  lastLogon: string;
  isAdmin: string;
  canUploadInventory: string;
  canEnterOrders: string;
  canAccessDashboard: string;
  canAccessPriceLists: string;
  canAccessForecast: string;
  canAccessForecastEmail: string;
}

type SortField = 'userId' | 'firstName' | 'lastName' | 'email' | 'phone' | 'numLogons' | 'lastLogon' | 'pwDate';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

const ChannelPartnerDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  // Removed pagination state variables
  const [ CP_UsersByIdSafe, setCP_UsersByIdSafe ] = useState<CPUsersDetails[]>([]);

  const { data: CP_Details, isFetched: hasFetched } =
    useGetCompanyById(id);
  const {
    data: CP_UsersById,
    isFetched: hasFetchedCPUsers,
    isLoading,
  } = useGetCompanyUsersById(id);

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // Sorting logic (removed pagination)
  const sortedUserList = useMemo(() => {
    const list = CP_UsersById || [];
    if (!sortState.field || !sortState.direction) return list;
    return [...list].sort((a, b) => {
      let aValue: any = a[sortState.field!];
      let bValue: any = b[sortState.field!];
      
      // Handle date fields
      if (sortState.field === 'lastLogon' || sortState.field === 'pwDate') {
        if (!aValue) aValue = '1900-01-01';
        if (!bValue) bValue = '1900-01-01';
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortState.field === 'numLogons') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (sortState.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [CP_UsersById, sortState]);

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortState.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortState.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  useEffect(() => {
    if (hasFetchedCPUsers && CP_UsersById) {
      setCP_UsersByIdSafe(CP_UsersById);
    }
  }, [hasFetchedCPUsers, CP_UsersById]);

  const navigateDeleteUser = (
    userId: string,
    navigateTo: string,
    firstName: string,
    lastName: string
  ) => {
    if (navigateTo === "delete") {
      const params = new URLSearchParams({
        firstName: firstName,
        lastName: lastName,
      }).toString();
      navigate(`/otherextranet/managecp/delete-user/${userId}?${params}`);
    }
  };

  const navigateEditUser = (
    userId: string,
    navigateTo: string,
    name: string,
    numLogons: string,
    lastLogon: string
  ) => {
    if (navigateTo === "edit") {
      const params = new URLSearchParams({
        name: name,
        numLogons: numLogons,
        lastLogon: lastLogon,
      }).toString();
      navigate(`/otherextranet/managecp/edit-user/${userId}?${params}`);
    }
  };

  return (
    <DashboardLayout
      titleIcon={<DesktopWindowsOutlinedIcon />}
      title="Other Extranet"
      initialBreadcrumbs="Extranet/Barksdale Admin"
      levelBreadcrumbs=""
      breadcrumbs="Channel Partner List"
      activeMenu="overview"
      scrollableWidth={isLoading ? '100%' : "89.2%"}
    >
      <Box sx ={{ width: "100%" }}>
        {!hasFetched || !CP_Details ? (
          <>
            <Skeleton width="38%" />
            <Skeleton width="100%" />
            <Skeleton width="100%" />
            <Skeleton width="61%" />
          </>
        ) : (
          <Box>
            {/* Heading */}
            <Typography variant="h6" fontWeight="bold" fontSize="1rem">
              Channel Partner Details for: {CP_Details.name}
            </Typography>

            {/* Content Section */}
            <Box
              mt={3}
              display="flex"
              flexDirection={{ xs: "row", md: "row" }}
              gap={4}
            >
              {/* Left Column */}
              <Box flex={1} display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Old Extranet Username: {CP_Details.oldName}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  Customer Number: {CP_Details.custNum}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  SalesPerson Number: {CP_Details.spNum}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  Date Added:{" "}
                  {moment(CP_Details.dateAdded).format("MM/DD/YYYY")}
                </Typography>
              </Box>

              {/* Divider */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: "none", md: "block" } }}
              />

              {/* Right Column */}
              <Box flex={1} display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Number of Logons: {CP_Details.numLogons}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  Last Logon: {" "}
                  {moment(CP_Details.lastLogon).format("MM/DD/YYYY")}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  Verify Date:{" "}
                  {moment(CP_Details.verifyDate).format("MM/DD/YYYY")}
                </Typography>
                <Typography variant="subtitle2" fontWeight="bold">
                  Verified By: {CP_Details.verifiedBy}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {CP_Details && CP_Details.id && CP_Details.name && (
        <Box sx={{ mt: 3 }}>
          <Typography>
            <Link
              to={`/OtherExtranet/manageCP/add-user?id=${
                CP_Details.id
              }&name=${encodeURIComponent(CP_Details.name)}`}
              style={{
                textDecoration: "underline",
                color: "#c3183c",
                cursor: "pointer",
              }}
            >
              Add User
            </Link>
          </Typography>
        </Box>
      )}
      <Box sx={{ mt: 3, width: "100%" }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 3, }}
        >
          <Table size="small" aria-label="user list table">
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('userId')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Id
                    <Tooltip title="Click to sort">{getSortIcon('userId')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('firstName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    First Name
                    <Tooltip title="Click to sort">{getSortIcon('firstName')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('lastName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Last Name
                    <Tooltip title="Click to sort">{getSortIcon('lastName')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell>Password</TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('pwDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    PW Date
                    <Tooltip title="Click to sort">{getSortIcon('pwDate')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('email')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Email
                    <Tooltip title="Click to sort">{getSortIcon('email')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('phone')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Phone
                    <Tooltip title="Click to sort">{getSortIcon('phone')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('numLogons')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    #Logons
                    <Tooltip title="Click to sort">{getSortIcon('numLogons')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('lastLogon')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Last Logon
                    <Tooltip title="Click to sort">{getSortIcon('lastLogon')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Upload</TableCell>
                <TableCell>Enter Orders</TableCell>
                <TableCell>Dashboard</TableCell>
                <TableCell>Price List</TableCell>
                <TableCell>Forecast</TableCell>
                <TableCell>Forecast Email</TableCell>
               
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={18}>
                    <Skeleton width="38%" />
                    <Skeleton width="100%" />
                    <Skeleton width="100%" />
                    <Skeleton width="61%" />
                  </TableCell>
                </TableRow>
              ) : hasFetchedCPUsers && sortedUserList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={18} sx={{ textAlign: "center" }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : (
                sortedUserList.map((CPUserDetails: CPUsersDetails) => (
                  <TableRow key={CPUserDetails.userId} hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="edit user"
                        onClick={() =>
                          navigateEditUser(
                            CPUserDetails.userId,
                            "edit",
                            CP_Details.name,
                            CPUserDetails.numLogons,
                            CPUserDetails.lastLogon
                          )
                        }
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="delete user"
                        onClick={() =>
                          navigateDeleteUser(
                            CPUserDetails.userId,
                            "delete",
                            CPUserDetails.firstName,
                            CPUserDetails.lastName
                          )
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>{CPUserDetails.userId}</TableCell>
                    <TableCell>{CPUserDetails.firstName}</TableCell>
                    <TableCell>{CPUserDetails.lastName}</TableCell>
                    <TableCell>{CPUserDetails.password}</TableCell>
                    <TableCell>
                      {moment(CPUserDetails.pwDate).format("MM/DD/YYYY")}
                    </TableCell>
                    <TableCell>{CPUserDetails.email}</TableCell>
                    <TableCell>{CPUserDetails.phone}</TableCell>
                    <TableCell>{String(CPUserDetails.numLogons)}</TableCell>
                    <TableCell>
                      {moment(CPUserDetails.lastLogon).format("MM/DD/YYYY")}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.isAdmin && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canUploadInventory && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canEnterOrders && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canAccessDashboard && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canAccessPriceLists && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canAccessForecast && <CheckIcon color="error" />}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {CPUserDetails.canAccessForecastEmail && <CheckIcon color="error" />}
                    </TableCell>
                    
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Back Button (removed pagination) */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Button
            variant="contained"
            sx={{ backgroundColor: "#c3183c", padding: "6px 25px !important" }}
            onClick={() => navigate(-1)}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ChannelPartnerDetails;
